import {
  Box,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import UserMentionManager from '../../../helpers/utils/UserMentionManager';

const TextFieldWithMentions = ({
  noteContent,
  setNoteContent,
  handleTaskConfirm,
}) => {
  const theme = useTheme();
  const users = useSelector((state) => state?.static?.users) || [];
  const userMentionManager = new UserMentionManager();
  const [mentionOpen, setMentionOpen] = useState(false);
  const [_mentionQuery, setMentionQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const mentionBoxRef = useRef(null);
  const textFieldRef = useRef(null);
  const selectedItemRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mentionBoxRef.current &&
        !mentionBoxRef.current.contains(event.target)
      ) {
        setMentionOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const mongoIdRegex = /[a-fA-F0-9]{24}/g;
    if (noteContent && mongoIdRegex.test(noteContent)) {
      const modifiedContent =
        userMentionManager.replaceIdsWithNames(noteContent);
      setNoteContent(modifiedContent);
    }
  }, [noteContent]);

  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedIndex]);

  const handleTextChange = (event) => {
    const newValue = event.target.value;
    setNoteContent(
      newValue.substring(
        0,
        import.meta.env.VITE_MAXIMUM_NOTE_CONTENT_SIZE || 500
      )
    );

    const match = newValue.match(/(^|\s)(@\w*)$/);

    if (match) {
      const search = match[2].slice(1).toLowerCase();
      setMentionQuery(search);
      setFilteredUsers(
        users.filter(
          (user) =>
            user.isActive &&
            (user.firstName.toLowerCase().includes(search) ||
              user.lastName.toLowerCase().includes(search) ||
              user.email.toLowerCase().includes(search))
        )
      );
      setMentionOpen(true);
      setSelectedIndex(0);
    } else {
      setMentionOpen(false);
    }
  };

  const handleSelectUser = (user) => {
    setNoteContent((prevContent) => {
      return prevContent.replace(/(^|\s)(@\w*)$/, `$1@${user._id} `);
    });

    setMentionOpen(false);
    setMentionQuery('');

    textFieldRef.current.focus();
  };

  const handleKeyDown = (event) => {
    if (mentionOpen) {
      if (event.key === 'ArrowDown') {
        setSelectedIndex((prevIndex) =>
          Math.min(filteredUsers.length - 1, prevIndex + 1)
        );
      } else if (event.key === 'ArrowUp') {
        setSelectedIndex((prevIndex) => Math.max(0, prevIndex - 1));
      } else if (
        event.key === 'Enter' ||
        event.key === 'Tab' ||
        event.key === ' '
      ) {
        handleSelectUser(filteredUsers[selectedIndex]);
        event.preventDefault();
        event.stopPropagation();
      } else if (event.key === 'Escape') {
        setMentionOpen(false);
      }
    } else {
      if (event.key === 'Enter') {
        if (event.ctrlKey) {
          // Ajout d'un saut de ligne sur Ctrl + Entrée
          setNoteContent((prev) => prev + '\n');
        } else {
          // Appel de handleTaskConfirm sur Entrée
          event.preventDefault();
          handleTaskConfirm();
        }
      }
    }
  };

  return (
    <Box style={{ position: 'relative' }}>
      <TextField
        ref={textFieldRef}
        fullWidth
        multiline
        minRows={3}
        value={noteContent}
        onChange={handleTextChange}
        placeholder="Ajoutez une note..."
        helperText={`${noteContent.length} / ${
          import.meta.env.VITE_UM_NOTE_CONTENT_SIZE || 500
        }`}
        error={noteContent.length === 0}
        onKeyDown={handleKeyDown}
      />
      <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
        Appuyez sur <strong>Entrée</strong> pour valider, ou{' '}
        <strong>Ctrl + Entrée</strong> pour un saut de ligne.
      </Typography>
      {mentionOpen && (
        <Paper
          ref={mentionBoxRef}
          style={{
            position: 'absolute',
            zIndex: 10,
            background: theme.palette.background.paper,
            boxShadow: '0px 4px 6px rgba(0,0,0,0.1)',
            maxHeight: '150px',
            overflowY: 'auto',
            width: '100%',
          }}
        >
          <List>
            {filteredUsers.map((user, index) => (
              <ListItem
                key={user._id}
                ref={index === selectedIndex ? selectedItemRef : null}
                selected={index === selectedIndex}
                onClick={() => handleSelectUser(user)}
                sx={[
                  {
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                    backgroundColor:
                      index === selectedIndex
                        ? theme.palette.action.selected
                        : theme.palette.background.default,
                  },
                ]}
              >
                <ListItemText primary={`${user.firstName} ${user.lastName}`} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default TextFieldWithMentions;
